import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Header from "components/Header";
import ControlButton from "components/IconButton";
import StepIndicator from "components/StepIndicator";
import AccountForm from "./components/AccountForm";
import ProfileForm from "./components/ProfileForm";
import PreferencesForm from "./components/PreferencesForm";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";
import { AppRoutes } from "pages/constants/routes";
import { setEmail, SignUpState } from "store/signUpSlice";
import AuthService from "api/Auth/AuthService";
import { RootState } from "store";
import { FormRef } from "types/form";

import styles from "./SignUp.module.scss";
import { ReactComponent as ArrowNext } from "assets/misc/arrow_right.svg";
import { ReactComponent as ArrowBack } from "assets/misc/arrow_left.svg";
import { ReactComponent as Tick } from "assets/misc/tick.svg";
import UserService from "api/User/UserService";
import { setUser } from "store/userSlice";

const SignUp = () => {
  const [stageIndex, setStageIndex] = useState<number>(0);
  const stages = ["Account", "Profile", "Preferences"];
  const [apiError, setApiError] = useState<string>("");

  const formRefs: Record<number, React.RefObject<FormRef>> = {
    0: useRef<FormRef>(null),
    1: useRef<FormRef>(null),
    2: useRef<FormRef>(null),
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getErrorMessage = (error: any): string => {
    switch (error?.status) {
      case 400:
        return "Email already exists. Please try again with a different email";
      case 404:
        return "Server error occurred. Please try again later";
      default:
        return "An error occurred during signup. Please try again";
    }
  };

  const signUpState: SignUpState = useSelector(
    (state: RootState) => state.signUp,
  );

  useEffect(() => {
    if (location.state?.email === undefined) {
      return;
    }

    dispatch(setEmail(location.state.email));
  }, [dispatch, location.state]);

  const handleBack = () => {
    setApiError("");

    if (stageIndex === 0) {
      navigate(AppRoutes.signin);
      return;
    }

    setStageIndex((stage) => Math.max(0, stage - 1));
  };

  const handleNext = async () => {
    try {
      const isValid = await formRefs[stageIndex].current?.validate();
      if (!isValid) return;

      if (stageIndex === stages.length - 1) {
        handleSubmit();
        return;
      }

      setStageIndex((stage) => Math.min(stages.length - 1, stage + 1));
    } catch (error) {
      setApiError((error as Error).message);
    }
  };

  const getDropdownValue = <T,>(
    dropdownSelected: ComboBoxItem<T>,
    other: T,
  ) => {
    if (dropdownSelected.value !== "Others") {
      return dropdownSelected.value;
    } else {
      return other;
    }
  };

  const handleSubmit = async () => {
    setApiError("");
    if (
      signUpState.industry === undefined ||
      signUpState.role === undefined ||
      signUpState.outreach === undefined ||
      signUpState.currentSolution === undefined ||
      signUpState.keyChallenges === undefined ||
      signUpState.teamSize === undefined
    ) {
      return;
    }

    try {
      const response = await AuthService.signup({
        email: signUpState.email,
        username: signUpState.username,
        password: signUpState.password,
        firstName: signUpState.firstName,
        lastName: signUpState.lastName,
        company: signUpState.company,
        industry: signUpState.industry.value,
        role: signUpState.role.value,
        outreach: getDropdownValue(
          signUpState.outreach,
          signUpState.outreachOthers,
        ),
        currentSolution: getDropdownValue(
          signUpState.currentSolution,
          signUpState.currentSolutionOthers,
        ),
        keyChallenges: getDropdownValue(
          signUpState.keyChallenges,
          signUpState.keyChallengesOthers,
        ),
        teamSize: signUpState.teamSize.value,
        referralCode: signUpState.referralCode,
      });
      if (response.success) {
        dispatch({ type: "signUp/reset" });
        const userResponse = await UserService.getSelf();
        dispatch(
          setUser({
            loggedIn: true,
            username: userResponse.username,
            email: userResponse.email,
            hasPrivilegedAccess: userResponse.has_privileged_access,
          }),
        );
        navigate(AppRoutes.home);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setApiError(getErrorMessage(err));
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background} />
      <Header hideSignIn />
      <div className={styles.content}>
        <div className={styles.signUpContainer}>
          <div className={styles.subheader}>
            <span className={styles.welcomeTitle}>Welcome!</span>
            <span className={styles.subtext}>
              We will just need some information from you to get started!
            </span>
            <StepIndicator stages={stages} stageIndex={stageIndex} />
            {apiError && (
              <div className={styles.errorContainer}>{apiError}</div>
            )}
          </div>
          <div className={styles.form}>
            {stageIndex === 0 ? (
              <AccountForm ref={formRefs[0]} />
            ) : stageIndex === 1 ? (
              <ProfileForm ref={formRefs[1]} />
            ) : (
              <PreferencesForm ref={formRefs[2]} />
            )}
          </div>
          <div className={styles.controls}>
            <ControlButton hoverColor="red" onClick={handleBack}>
              <ArrowBack />
            </ControlButton>
            <ControlButton hoverColor="green" onClick={() => handleNext()}>
              {stageIndex < stages.length - 1 ? <ArrowNext /> : <Tick />}
            </ControlButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
